import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  LinearProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { Email as EmailIcon, CheckCircle as CheckCircleIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import { supabase } from '../lib/supabase';

const COOLDOWN_MINUTES = 2; // Wait 2 minutes between requests
const COOLDOWN_MS = COOLDOWN_MINUTES * 60 * 1000;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [lastRequestTime, setLastRequestTime] = useState<number | null>(null);

  // Check cooldown on mount and when email changes
  useEffect(() => {
    if (!email) {
      setCooldownRemaining(0);
      return;
    }

    const storageKey = `password_reset_cooldown_${email}`;
    const lastRequest = localStorage.getItem(storageKey);

    if (lastRequest) {
      const lastRequestTimestamp = parseInt(lastRequest, 10);
      const now = Date.now();
      const timeSinceRequest = now - lastRequestTimestamp;
      const remaining = COOLDOWN_MS - timeSinceRequest;

      if (remaining > 0) {
        setLastRequestTime(lastRequestTimestamp);
        setCooldownRemaining(Math.ceil(remaining / 1000)); // Convert to seconds
      } else {
        // Cooldown expired, clear storage
        localStorage.removeItem(storageKey);
        setCooldownRemaining(0);
        setLastRequestTime(null);
      }
    }
  }, [email]);

  // Countdown timer
  useEffect(() => {
    if (cooldownRemaining <= 0) return;

    const timer = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) {
          // Cooldown finished
          const storageKey = `password_reset_cooldown_${email}`;
          localStorage.removeItem(storageKey);
          setLastRequestTime(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownRemaining, email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Check cooldown before submitting
    if (cooldownRemaining > 0) {
      setError(`Please wait ${Math.ceil(cooldownRemaining / 60)} more minute(s) before requesting another reset.`);
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      // Store timestamp in localStorage
      const now = Date.now();
      const storageKey = `password_reset_cooldown_${email}`;
      localStorage.setItem(storageKey, now.toString());
      setLastRequestTime(now);
      setCooldownRemaining(COOLDOWN_MS / 1000);

      setSuccess(true);
    } catch (error: any) {
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Check Your Email
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We've sent a password reset link to <strong>{email}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Click the link in the email to reset your password. If you don't see it, check your spam folder.
            </Typography>
            <Button
              component={Link}
              to="/login"
              variant="outlined"
              fullWidth
            >
              Back to Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <EmailIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
            <Typography component="h1" variant="h4" align="center" gutterBottom>
              Forgot Password?
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {cooldownRemaining > 0 && (
            <Alert
              severity="info"
              icon={<ScheduleIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Password reset recently requested
              </Typography>
              <Typography variant="body2">
                Please wait for the email to arrive before trying again.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                You can request another reset in: <strong>{formatTime(cooldownRemaining)}</strong>
              </Typography>
              <LinearProgress
                variant="determinate"
                value={((COOLDOWN_MS / 1000 - cooldownRemaining) / (COOLDOWN_MS / 1000)) * 100}
                sx={{ mt: 1 }}
              />
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || cooldownRemaining > 0}
            >
              {loading
                ? 'Sending...'
                : cooldownRemaining > 0
                ? `Wait ${formatTime(cooldownRemaining)}`
                : 'Send Reset Link'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link to="/login" style={{ textDecoration: 'none', fontSize: '0.875rem' }}>
                Back to Login
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
